import { useMemo, useState } from "react";
import {
  Briefcase,
  ChevronDown,
  Layers,
  MessageSquarePlus,
  Sparkles,
  X,
} from "lucide-react";
import ChatInterface from "@/components/ai/chat-interface";
import ChatSessionsList from "@/components/ai/chat-sessions-list";
import { useLicitaciones } from "@/hooks/use-licitaciones";
import type { ChatFamilia } from "@/types/chat";

// Mejora "Asistente con ámbito" (changelog cliente): rediseño completo
// del chat global. Tres cambios visibles:
//
//   1. UN solo botón "Nueva conversación" (antes había dos solapados).
//   2. Selector de licitación en la cabecera (antes el usuario no sabía
//      sobre qué hablaba la IA si llegaba desde la home).
//   3. Segmented control "Todo / Pliego / Empresa" que acota el RAG a
//      una familia de documentos. Es transparente: el usuario VE qué
//      ámbito está consultando la IA en cada momento.

const FAMILIA_OPTIONS: Array<{
  value: ChatFamilia | "all";
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    value: "all",
    label: "Todo",
    hint: "Pliego + documentos de la empresa",
    icon: Layers,
  },
  {
    value: "bases",
    label: "Pliego",
    hint: "Bases de la convocatoria",
    icon: Sparkles,
  },
  {
    value: "apoyo",
    label: "Empresa",
    hint: "Memoria, casos de éxito, certificaciones",
    icon: Briefcase,
  },
];

function getSuggestions(
  licitacionSelected: boolean,
  familia: "all" | ChatFamilia
): string[] {
  if (!licitacionSelected) {
    return [
      "¿Qué licitaciones tengo con plazo en los próximos 14 días?",
      "Resume el estado de mis propuestas en curso",
      "¿Qué tipos de licitación hemos ganado más en los últimos meses?",
    ];
  }
  if (familia === "apoyo") {
    return [
      "¿Qué experiencia tenemos en proyectos similares?",
      "Lista las certificaciones de la empresa aplicables al pliego",
      "¿Hemos presentado memorias parecidas a esta?",
    ];
  }
  if (familia === "bases") {
    return [
      "¿Cuál es el presupuesto base y la duración?",
      "¿Qué personal mínimo exige el pliego?",
      "Lista los criterios de adjudicación con su ponderación",
    ];
  }
  return [
    "¿Cumplimos los requisitos de personal del pliego con el equipo actual?",
    "¿Qué diferencias hay con la licitación anterior?",
    "Sugiere mejoras técnicas que podríamos ofertar",
  ];
}

export default function ChatPage() {
  const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [licitacionId, setLicitacionId] = useState<string | undefined>();
  const [familiaScope, setFamiliaScope] = useState<"all" | ChatFamilia>("all");
  const [licitacionMenuOpen, setLicitacionMenuOpen] = useState(false);

  // Catálogo de licitaciones para el selector. El backend respeta team
  // visibility, así que ve solo las que puede consultar.
  const { data: licitacionesData } = useLicitaciones({ page: 1, page_size: 100 });
  const licitaciones = licitacionesData?.data ?? [];

  const licitacionSeleccionada = useMemo(
    () => licitaciones.find((l) => l.id === licitacionId),
    [licitaciones, licitacionId]
  );

  // Mapa rápido id → título para que ChatSessionsList muestre el
  // nombre de la licitación en cada sesión.
  const licitacionesById = useMemo(
    () => Object.fromEntries(licitaciones.map((l) => [l.id, l.titulo])),
    [licitaciones]
  );

  const handleNewChat = () => setSelectedSessionId(undefined);

  const handleSessionCreated = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setRefreshKey((p) => p + 1);
  };

  const handleMessageSent = () => setRefreshKey((p) => p + 1);

  const familiaForRequest: ChatFamilia | undefined =
    familiaScope === "all" ? undefined : familiaScope;

  const suggestions = useMemo(
    () => getSuggestions(!!licitacionId, familiaScope),
    [licitacionId, familiaScope]
  );

  return (
    <div className="h-[calc(100vh-3.5rem)] p-6 flex flex-col">
      <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
        {/* Lista de conversaciones */}
        <aside className="col-span-3 flex flex-col min-h-0">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h1 className="text-2xl font-heading font-semibold text-ink truncate">
              Asistente
            </h1>
            <button
              onClick={handleNewChat}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white border border-brand-border rounded text-xs font-heading font-semibold hover:bg-brand-hover transition-colors whitespace-nowrap"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              Nueva
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ChatSessionsList
              key={refreshKey}
              selectedSessionId={selectedSessionId}
              onSessionSelect={setSelectedSessionId}
              showNewButton={false}
              licitacionesById={licitacionesById}
            />
          </div>
        </aside>

        {/* Chat principal */}
        <main className="col-span-9 min-h-0 flex flex-col bg-surface border border-line rounded shadow-card overflow-hidden">
          {/* Cabecera: licitación + ámbito */}
          <div className="px-5 py-3 border-b border-line flex items-center gap-4 flex-wrap">
            {/* Selector de licitación */}
            <div className="relative">
              <button
                onClick={() => setLicitacionMenuOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-line bg-surface hover:border-brand-muted text-sm transition-colors max-w-md"
                aria-haspopup="menu"
                aria-expanded={licitacionMenuOpen}
              >
                <span className="text-content-muted text-xs uppercase tracking-wider font-heading font-semibold">
                  Licitación:
                </span>
                <span className="font-heading font-semibold text-ink truncate">
                  {licitacionSeleccionada?.titulo ?? "Todas"}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-content-muted flex-shrink-0" />
              </button>
              {licitacionMenuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 top-full mt-1 w-80 bg-surface border border-line rounded shadow-card z-30 max-h-80 overflow-y-auto"
                  onMouseLeave={() => setLicitacionMenuOpen(false)}
                >
                  <button
                    onClick={() => {
                      setLicitacionId(undefined);
                      setLicitacionMenuOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-surface-100 ${!licitacionId ? "font-semibold bg-surface-100" : ""}`}
                  >
                    Todas las licitaciones
                  </button>
                  <div className="border-t border-line" />
                  {licitaciones.length === 0 && (
                    <div className="px-3 py-2 text-xs text-content-muted">
                      No tienes licitaciones visibles.
                    </div>
                  )}
                  {licitaciones.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => {
                        setLicitacionId(l.id);
                        setLicitacionMenuOpen(false);
                      }}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-surface-100 ${l.id === licitacionId ? "bg-brand/8 text-brand-hover" : ""}`}
                    >
                      <div className="font-heading font-semibold text-ink truncate">
                        {l.titulo}
                      </div>
                      <div className="text-xs text-content-muted truncate">
                        {l.organismo}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {licitacionSeleccionada && (
                <button
                  onClick={() => setLicitacionId(undefined)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-surface border border-line rounded-full grid place-items-center text-content-muted hover:text-danger hover:border-danger"
                  aria-label="Quitar licitación del contexto"
                  title="Quitar licitación del contexto"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>

            {/* Segmented control de ámbito (familia) */}
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-heading font-semibold text-content-muted">
                Buscar en:
              </span>
              <div
                role="radiogroup"
                aria-label="Ámbito de búsqueda"
                className="inline-flex border border-line rounded overflow-hidden"
              >
                {FAMILIA_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = familiaScope === opt.value;
                  return (
                    <button
                      key={opt.value}
                      role="radio"
                      aria-checked={active}
                      onClick={() => setFamiliaScope(opt.value)}
                      title={opt.hint}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading font-semibold transition-colors border-r border-line last:border-r-0 ${active ? "bg-brand text-white" : "bg-surface text-content hover:bg-surface-100"}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <ChatInterface
              key={`${selectedSessionId ?? "new"}-${licitacionId ?? "all"}-${familiaScope}`}
              sessionId={selectedSessionId}
              licitacionId={licitacionId}
              familia={familiaForRequest}
              hideHeader
              emptyState={
                <div className="flex flex-col items-center text-center pt-10 px-6">
                  <div className="w-12 h-12 rounded-full bg-brand/12 grid place-items-center mb-3">
                    <Sparkles className="w-5 h-5 text-brand" />
                  </div>
                  <h2 className="font-heading font-semibold text-lg text-ink mb-1">
                    ¿Por dónde empezamos?
                  </h2>
                  <p className="text-sm text-content-muted max-w-md">
                    {licitacionSeleccionada
                      ? `Voy a buscar en ${familiaScope === "bases" ? "el pliego" : familiaScope === "apoyo" ? "los documentos de empresa" : "todos los documentos"} de "${licitacionSeleccionada.titulo}".`
                      : "Selecciona una licitación arriba o haz una pregunta general sobre tu cartera."}
                  </p>
                  <div className="mt-5 flex flex-col gap-2 w-full max-w-md">
                    {suggestions.map((q) => (
                      <span
                        key={q}
                        className="text-left px-3 py-2 text-sm rounded border border-line bg-surface text-content-muted"
                      >
                        {q}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-content-muted">
                    Copia o reescribe una sugerencia en el campo de abajo y
                    pulsa Enviar.
                  </p>
                </div>
              }
              onSessionCreated={handleSessionCreated}
              onMessageSent={handleMessageSent}
            />
          </div>
        </main>
      </div>
    </div>
  );
}
